import React from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tooltip } from "primereact/tooltip";
import { ArrowsInSimple, ArrowsOutSimple, FloppyDisk, MapPin } from "@phosphor-icons/react";
import hallticketWordmarkDark from "../../../assets/design/HallTicket logo/wordmark/dark/hallticket-wordmark-256-dark.png";
import {
  fetchApplicantProfile,
  getApplicantFromStorage,
  type Applicant,
  type HapTableCell,
  type HapTableCollege,
  type HapTableCourse,
  type HapTableResponse,
  useAuthState,
} from "../../../hallticket-lib";
import { saveDomNodeAsPdf } from "../../../utils/saveDomAsPdf";
import { HapEmptyFiltered, HapEmptyNoData } from "./ResultReviewEmptyState";

export type ResultReviewSort = "avg" | "name";
type ResultReviewFilterMode = "course" | "college";
const SELECT_ALL_COLLEGES_VALUE = "__select_all_colleges__";
const SELECT_ALL_COURSES_VALUE = "__select_all_courses__";
const ALL_LOCATIONS_VALUE = "__all_locations__";
const PDF_EXPORT_MAX_COURSES = 8;
const CUTOFF_POPOVER_WIDTH = 288;

type ScoreUnit = "ile" | "pts";

interface CutoffTrendPoint {
  year: number;
  value: number;
}

interface HoveredCutoffCell {
  anchorRect: DOMRect;
  collegeName: string;
  location: string;
  branch: string;
  probability: number;
  latestCutoff: number;
  latestYear: number;
  yourScore: number;
  unit: ScoreUnit;
  trend: CutoffTrendPoint[];
  cutoffDataYears: number;
}

interface HapHeatmapPanelProps {
  data: HapTableResponse;
  status: "idle" | "loading" | "success" | "error";
  title?: string;
  subtitle?: string;
  loadingOverlayText: string;
  sortMode: ResultReviewSort;
  courseFilter: string[];
  courseFilterOptions?: string[];
  disabledCourseFilterOptions?: string[];
  onSortModeChange: (value: ResultReviewSort) => void;
  onCourseFilterChange: (value: string[]) => void;
  filterMode?: ResultReviewFilterMode;
  collegeFilter?: string[];
  onCollegeFilterChange?: (value: string[]) => void;
  statText?: string;
  showLocationFilter?: boolean;
  locationFilter?: string;
  onLocationFilterChange?: (value: string) => void;
  trailingActionButton?: React.ReactNode;
}

function formatPercentile(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : "0.00";
}

function resolveScoreUnit(exam: string | undefined): ScoreUnit {
  return exam?.toLowerCase().includes("bitsat") ? "pts" : "ile";
}

function buildFallbackCutoffTrend(cutoffValue: number, endYear: number): CutoffTrendPoint[] {
  const k = cutoffValue > 200 ? 5 : 1;
  const offsets = [-1.1, -0.8, -0.5, -0.2, 0];
  return offsets.map((offset, index) => ({
    year: endYear - (offsets.length - 1) + index,
    value: Number((cutoffValue + offset * k + Math.sin(cutoffValue * 13.13 + index * 7.7) * 0.18 * k).toFixed(2)),
  }));
}

function resolveCutoffTrend(cell: HapTableCell, endYear: number): CutoffTrendPoint[] {
  const yearlyCutoffs = cell.yearly_cutoffs ?? [];
  if (yearlyCutoffs.length >= 2) {
    return yearlyCutoffs
      .map((entry) => ({
        year: entry.year,
        value: entry.cutoff_percentile,
      }))
      .sort((a, b) => a.year - b.year)
      .slice(-5);
  }

  return buildFallbackCutoffTrend(cell.cutoff_percentile, endYear);
}

function getProbabilityPillColors(probability: number): { foreground: string; background: string } {
  if (probability > 60) {
    return { foreground: "#2F6A43", background: "rgba(47,106,67,.13)" };
  }
  if (probability > 40) {
    return { foreground: "#B8651F", background: "rgba(184,101,31,.15)" };
  }
  return { foreground: "#8C2E24", background: "rgba(140,46,36,.13)" };
}

function getCollegeLocation(college: HapTableCollege): string {
  if (college.city?.trim()) {
    return college.city.trim();
  }
  const collegeName = college.college_name;
  const parts = collegeName.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
  }
  if (parts.length === 2) {
    return parts[1];
  }
  return "Maharashtra";
}

function getProbabilityBand(probability: number): 1 | 2 | 3 | 4 {
  if (probability >= 75) return 4;
  if (probability >= 50) return 3;
  if (probability >= 25) return 2;
  return 1;
}

function getCourseTableHeading(course: HapTableCourse | undefined): string {
  if (!course) {
    return "";
  }
  const shortform = course.course_shortform?.trim();
  return shortform || course.course_name;
}

function applicantDisplayNameForPdf(applicant: Applicant | null): string {
  if (!applicant) {
    return "HallTicket user";
  }
  const first = applicant.firstName?.trim() ?? "";
  const last = applicant.lastName?.trim() ?? "";
  const combined = `${first} ${last}`.trim();
  if (combined.length > 0) {
    return combined;
  }
  const email = applicant.email?.trim();
  if (email) {
    return email;
  }
  return "HallTicket user";
}

function hasApplicantIdentity(applicant: Applicant | null): boolean {
  if (!applicant) {
    return false;
  }
  const first = applicant.firstName?.trim() ?? "";
  const last = applicant.lastName?.trim() ?? "";
  if (`${first} ${last}`.trim().length > 0) {
    return true;
  }
  return Boolean(applicant.email?.trim());
}

export function HapHeatmapPanel({
  data,
  status,
  title,
  subtitle,
  loadingOverlayText,
  sortMode,
  courseFilter,
  courseFilterOptions,
  disabledCourseFilterOptions = [],
  onSortModeChange,
  onCourseFilterChange,
  filterMode = "course",
  collegeFilter = [],
  onCollegeFilterChange,
  statText,
  showLocationFilter = false,
  locationFilter = ALL_LOCATIONS_VALUE,
  onLocationFilterChange,
  trailingActionButton,
}: HapHeatmapPanelProps) {
  const { applicant } = useAuthState();
  const pdfNameSourceApplicant = React.useMemo(
    () => (hasApplicantIdentity(applicant) ? applicant : getApplicantFromStorage()),
    [applicant],
  );
  const pdfExporterDisplayName = React.useMemo(
    () => applicantDisplayNameForPdf(pdfNameSourceApplicant),
    [pdfNameSourceApplicant],
  );
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [savingPdf, setSavingPdf] = React.useState(false);
  const [pdfExportDialogVisible, setPdfExportDialogVisible] = React.useState(false);
  const [pdfExportSelectedCourses, setPdfExportSelectedCourses] = React.useState<string[]>([]);
  const [hoveredCutoffCell, setHoveredCutoffCell] = React.useState<HoveredCutoffCell | null>(null);
  const [cutoffPopoverPosition, setCutoffPopoverPosition] = React.useState<{
    left: number;
    top: number;
    arrow: number;
    placement: "above" | "below";
  }>({
    left: 0,
    top: 0,
    arrow: CUTOFF_POPOVER_WIDTH / 2,
    placement: "above",
  });
  const pdfCaptureRef = React.useRef<HTMLDivElement>(null);
  const cutoffPopoverRef = React.useRef<HTMLDivElement>(null);
  const pdfExportCourseIndexRef = React.useRef<Set<number> | null>(null);
  const cutoffChartGradientId = React.useId();
  const resolvedCourseFilterOptions = React.useMemo(() => {
    if (courseFilterOptions && courseFilterOptions.length > 0) {
      return Array.from(new Set(courseFilterOptions.map((name) => name.trim()).filter(Boolean)));
    }
    return data.courses.map((course) => course.course_name);
  }, [courseFilterOptions, data.courses]);
  const allCourseNames = React.useMemo(
    () => resolvedCourseFilterOptions.map((name) => name),
    [resolvedCourseFilterOptions],
  );
  const disabledCourseNameSet = React.useMemo(
    () => new Set(disabledCourseFilterOptions.map((name) => name.trim().toLowerCase()).filter(Boolean)),
    [disabledCourseFilterOptions],
  );
  const selectableCourseNames = React.useMemo(
    () => allCourseNames.filter((name) => !disabledCourseNameSet.has(name.trim().toLowerCase())),
    [allCourseNames, disabledCourseNameSet],
  );
  const courseFilterDropdownOptions = React.useMemo(
    () => {
      return [
        { label: "Select all", value: SELECT_ALL_COURSES_VALUE },
        ...allCourseNames.map((name) => ({
          label: name,
          value: name,
          disabled: disabledCourseNameSet.has(name.trim().toLowerCase()),
        })),
      ];
    },
    [allCourseNames, disabledCourseNameSet],
  );
  const isAllCoursesSelected =
    selectableCourseNames.length > 0
    && selectableCourseNames.every((name) => courseFilter.includes(name));
  const courseMultiSelectValue = React.useMemo(
    () => (isAllCoursesSelected ? [SELECT_ALL_COURSES_VALUE, ...courseFilter] : courseFilter),
    [courseFilter, isAllCoursesSelected],
  );

  React.useEffect(() => {
    if (!isFullscreen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isFullscreen]);

  const visibleCourseIndexes = React.useMemo(() => {
    return data.courses
      .map((course, idx) => ({ course, idx }))
      .filter(({ course }) => courseFilter.length === 0 || courseFilter.includes(course.course_name))
      .map(({ idx }) => idx);
  }, [courseFilter, data.courses]);

  const locationOptions = React.useMemo(() => {
    const uniqueLocations = Array.from(
      new Set(data.colleges.map((college) => getCollegeLocation(college)).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));

    return [
      { label: "All Locations", value: ALL_LOCATIONS_VALUE },
      ...uniqueLocations.map((location) => ({ label: location, value: location })),
    ];
  }, [data.colleges]);

  React.useEffect(() => {
    if (!showLocationFilter || !onLocationFilterChange) {
      return;
    }
    const locationExists = locationOptions.some((option) => option.value === locationFilter);
    if (!locationExists) {
      onLocationFilterChange(ALL_LOCATIONS_VALUE);
    }
  }, [locationFilter, locationOptions, onLocationFilterChange, showLocationFilter]);

  const pdfExportCourseOptions = React.useMemo(
    () =>
      visibleCourseIndexes
        .map((idx) => {
          const label = data.courses[idx]?.course_name ?? "";
          return { label, value: label };
        })
        .filter((o) => o.value.length > 0),
    [data.courses, visibleCourseIndexes],
  );

  const resultRows = React.useMemo(() => {
    const rows = data.colleges.map((college) => {
      const candidateCells = visibleCourseIndexes
        .map((idx) => college.cells[idx])
        .filter((cell): cell is HapTableCell => cell !== null);

      const avg =
        candidateCells.length > 0
          ? candidateCells.reduce((sum, cell) => sum + cell.hap_probability, 0) / candidateCells.length
          : 0;

      return { college, averageProbability: avg };
    });

    const locationFilteredRows =
      showLocationFilter && locationFilter !== ALL_LOCATIONS_VALUE
        ? rows.filter((row) => getCollegeLocation(row.college) === locationFilter)
        : rows;
    const filteredRows =
      filterMode === "college"
        ? locationFilteredRows.filter((row) => collegeFilter.includes(row.college.college_name))
        : locationFilteredRows;

    if (sortMode === "name") {
      return filteredRows.sort((a, b) => a.college.college_name.localeCompare(b.college.college_name));
    }

    return filteredRows.sort((a, b) => b.averageProbability - a.averageProbability);
  }, [collegeFilter, data.colleges, filterMode, locationFilter, showLocationFilter, sortMode, visibleCourseIndexes]);

  const collegeOptions = React.useMemo(
    () =>
      data.colleges
        .map((college) => college.college_name)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    [data.colleges],
  );
  const allCollegeNames = React.useMemo(
    () => collegeOptions.map((option) => option.value),
    [collegeOptions],
  );
  const collegeFilterOptions = React.useMemo(
    () => [{ label: "Select all", value: SELECT_ALL_COLLEGES_VALUE }, ...collegeOptions],
    [collegeOptions],
  );
  const isAllCollegesSelected = allCollegeNames.length > 0 && collegeFilter.length === allCollegeNames.length;
  const collegeMultiSelectValue = React.useMemo(
    () => (isAllCollegesSelected ? [SELECT_ALL_COLLEGES_VALUE, ...collegeFilter] : collegeFilter),
    [collegeFilter, isAllCollegesSelected],
  );

  // Prefer showing college rows when the API returns colleges even with no courses or empty cells
  // (e.g. preferred-table with shortlisted colleges but no matrix data yet).
  const noTableData = data.colleges.length === 0;
  const filterExcludesAll = !noTableData && resultRows.length === 0;
  const showHeatmapTable = !noTableData && !filterExcludesAll;

  const handleResetFilters = React.useCallback(() => {
    onCourseFilterChange([]);
    if (filterMode === "college" && onCollegeFilterChange) {
      onCollegeFilterChange(allCollegeNames);
    }
    if (showLocationFilter && onLocationFilterChange) {
      onLocationFilterChange(ALL_LOCATIONS_VALUE);
    }
  }, [allCollegeNames, filterMode, onCollegeFilterChange, onCourseFilterChange, onLocationFilterChange, showLocationFilter]);

  const preparePdfClone = React.useCallback((clonedDoc: Document, clonedCaptureRoot: HTMLElement) => {
    if (clonedCaptureRoot instanceof HTMLElement) {
      clonedCaptureRoot.style.width = "max-content";
      clonedCaptureRoot.style.maxWidth = "none";
      clonedCaptureRoot.style.overflow = "visible";
    }
    clonedDoc.querySelectorAll(".rr-heatmap-wrap").forEach((node) => {
      if (node instanceof HTMLElement) {
        node.style.overflow = "visible";
        node.style.overflowX = "visible";
        node.style.overflowY = "visible";
        node.style.maxHeight = "none";
        node.style.height = "auto";
        node.style.width = "max-content";
        node.style.maxWidth = "none";
      }
    });
    clonedDoc.querySelectorAll(".rr-heatmap-table").forEach((node) => {
      if (node instanceof HTMLElement) {
        node.style.width = "max-content";
        node.style.minWidth = "max-content";
      }
    });
    clonedDoc.querySelectorAll(".rr-heatmap-pdf-letterhead").forEach((node) => {
      if (node instanceof HTMLElement) {
        node.style.display = "flex";
      }
    });
    clonedDoc.querySelectorAll(".rr-heatmap-pdf-footer").forEach((node) => {
      if (node instanceof HTMLElement) {
        node.style.display = "flex";
      }
    });

    const allowedIdx = pdfExportCourseIndexRef.current;
    if (allowedIdx && allowedIdx.size > 0) {
      clonedDoc.querySelectorAll("[data-rr-pdf-course-idx]").forEach((node) => {
        const raw = node.getAttribute("data-rr-pdf-course-idx");
        const idx = raw != null ? Number.parseInt(raw, 10) : NaN;
        if (Number.isFinite(idx) && !allowedIdx.has(idx) && node instanceof HTMLElement) {
          node.style.display = "none";
        }
      });

      const selectedCourseIndexes = Array.from(allowedIdx.values());
      clonedDoc.querySelectorAll(".rr-heatmap-table").forEach((tableNode) => {
        if (!(tableNode instanceof HTMLTableElement)) {
          return;
        }

        const headerRow = tableNode.querySelector("thead tr");
        if (headerRow instanceof HTMLTableRowElement) {
          const allHeaderCells = Array.from(headerRow.querySelectorAll("th"));
          const collegeHeaderCell = allHeaderCells.find(
            (cell) => !cell.hasAttribute("data-rr-pdf-course-idx") && !cell.classList.contains("rr-avg-col"),
          ) ?? null;
          const avgHeaderCell = allHeaderCells.find((cell) => cell.classList.contains("rr-avg-col")) ?? null;
          const courseHeaderCellsByIndex = new Map<number, HTMLTableCellElement>();

          allHeaderCells.forEach((cell) => {
            const raw = cell.getAttribute("data-rr-pdf-course-idx");
            if (raw == null) {
              return;
            }
            const idx = Number.parseInt(raw, 10);
            if (Number.isFinite(idx)) {
              courseHeaderCellsByIndex.set(idx, cell);
            }
          });

          while (headerRow.firstChild) {
            headerRow.removeChild(headerRow.firstChild);
          }

          if (collegeHeaderCell) {
            headerRow.appendChild(collegeHeaderCell);
          }

          selectedCourseIndexes.forEach((idx) => {
            const existing = courseHeaderCellsByIndex.get(idx);
            if (existing) {
              existing.style.removeProperty("display");
              headerRow.appendChild(existing);
            }
          });

          if (avgHeaderCell) {
            headerRow.appendChild(avgHeaderCell);
          }
        }

        const bodyRows = Array.from(tableNode.querySelectorAll("tbody tr"));
        bodyRows.forEach((rowNode) => {
          if (!(rowNode instanceof HTMLTableRowElement)) {
            return;
          }

          const allCells = Array.from(rowNode.querySelectorAll("td"));
          const collegeCell = allCells.find((cell) => !cell.hasAttribute("data-rr-pdf-course-idx")) ?? null;
          const avgCell = allCells.find((cell) => cell.classList.contains("rr-avg-cell")) ?? null;
          const courseCellsByIndex = new Map<number, HTMLTableCellElement>();

          allCells.forEach((cell) => {
            const raw = cell.getAttribute("data-rr-pdf-course-idx");
            if (raw == null) {
              return;
            }
            const idx = Number.parseInt(raw, 10);
            if (Number.isFinite(idx)) {
              courseCellsByIndex.set(idx, cell);
            }
          });

          while (rowNode.firstChild) {
            rowNode.removeChild(rowNode.firstChild);
          }

          if (collegeCell) {
            rowNode.appendChild(collegeCell);
          }

          selectedCourseIndexes.forEach((idx) => {
            const existing = courseCellsByIndex.get(idx);
            if (existing) {
              existing.style.removeProperty("display");
              rowNode.appendChild(existing);
              return;
            }

            const placeholderCell = clonedDoc.createElement("td");
            placeholderCell.className = "rr-heat-cell rr-cell-na";
            placeholderCell.setAttribute("data-rr-pdf-course-idx", String(idx));
            const placeholderText = clonedDoc.createElement("span");
            placeholderText.className = "rr-cell-pct";
            placeholderText.textContent = "—";
            placeholderCell.appendChild(placeholderText);
            rowNode.appendChild(placeholderCell);
          });

          if (avgCell) {
            rowNode.appendChild(avgCell);
          }
        });
      });
    }
  }, []);

  const openPdfExportDialog = React.useCallback(() => {
    if (savingPdf) {
      return;
    }
    const defaultNames = visibleCourseIndexes
      .slice(0, PDF_EXPORT_MAX_COURSES)
      .map((idx) => data.courses[idx]?.course_name ?? "")
      .filter((name) => name.length > 0);
    setPdfExportSelectedCourses(defaultNames);
    setPdfExportDialogVisible(true);
  }, [data.courses, savingPdf, visibleCourseIndexes]);

  const handlePdfExportConfirm = React.useCallback(async () => {
    if (pdfExportSelectedCourses.length === 0) {
      return;
    }

    const idxSet = new Set<number>();
    for (const name of pdfExportSelectedCourses) {
      const idx = visibleCourseIndexes.find((i) => (data.courses[i]?.course_name ?? "") === name);
      if (idx !== undefined) {
        idxSet.add(idx);
      }
    }
    if (idxSet.size === 0) {
      return;
    }

    const root = pdfCaptureRef.current;
    if (!root || !showHeatmapTable || status === "loading") {
      return;
    }

    // Profile hydration can finish after the table is interactive. If identity is still missing,
    // refresh profile once and wait one paint so the letterhead name is rendered before capture.
    if (!hasApplicantIdentity(applicant) && !hasApplicantIdentity(getApplicantFromStorage())) {
      try {
        await fetchApplicantProfile();
      } catch {
        // Keep export resilient; fallback label still applies if profile fetch fails.
      }
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
    }

    pdfExportCourseIndexRef.current = idxSet;

    const exportAllVisible =
      pdfExportSelectedCourses.length === visibleCourseIndexes.length
      && visibleCourseIndexes.every((idx) => pdfExportSelectedCourses.includes(data.courses[idx]?.course_name ?? ""));

    setSavingPdf(true);
    setPdfExportDialogVisible(false);
    try {
      const wrap = root.querySelector<HTMLElement>(".rr-heatmap-wrap");
      const table = root.querySelector<HTMLElement>(".rr-heatmap-table");
      const box = root.getBoundingClientRect();
      const rasterWidth = Math.ceil(
        Math.max(root.scrollWidth, wrap?.scrollWidth ?? 0, table?.scrollWidth ?? 0, box.width) + 8,
      );
      const rasterHeight = Math.ceil(
        Math.max(root.scrollHeight, wrap?.scrollHeight ?? 0, table?.scrollHeight ?? 0, box.height) + 8,
      );
      await saveDomNodeAsPdf(root, {
        ignoreElement: (node) => node.classList.contains("rr-pdf-exclude"),
        onClone: preparePdfClone,
        ...(exportAllVisible ? { width: rasterWidth, height: rasterHeight } : {}),
      });
    } catch (error) {
      console.error(error);
      window.alert("Could not save PDF. Try again or adjust your course selection.");
    } finally {
      pdfExportCourseIndexRef.current = null;
      setSavingPdf(false);
    }
  }, [
    data.courses,
    pdfExportSelectedCourses,
    preparePdfClone,
    applicant,
    showHeatmapTable,
    status,
    visibleCourseIndexes,
  ]);

  const canSavePdf = showHeatmapTable && status !== "loading";
  const trendEndYear = new Date().getFullYear() - 1;
  const hoveredTrendDelta = hoveredCutoffCell
    ? hoveredCutoffCell.trend[hoveredCutoffCell.trend.length - 1].value - hoveredCutoffCell.trend[0].value
    : 0;

  const cutoffPopoverChart = React.useMemo(() => {
    if (!hoveredCutoffCell || hoveredCutoffCell.trend.length < 2) {
      return null;
    }

    const chartWidth = 256;
    const chartHeight = 84;
    const padLeft = 4;
    const padRight = 4;
    const values = hoveredCutoffCell.trend.map((point) => point.value).concat([hoveredCutoffCell.yourScore]);
    let min = Math.min(...values);
    let max = Math.max(...values);
    const pad = (max - min) * 0.18 || 1;
    min -= pad;
    max += pad;

    const x = (index: number) =>
      padLeft + (index * (chartWidth - padLeft - padRight)) / (hoveredCutoffCell.trend.length - 1);
    const y = (value: number) => chartHeight - ((value - min) / (max - min)) * chartHeight;
    const points = hoveredCutoffCell.trend.map((point, index) => ({
      x: x(index),
      y: y(point.value),
      year: point.year,
      value: point.value,
    }));
    const linePath = points
      .map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
      .join(" ");
    const areaPath = `M${points[0].x.toFixed(1)} ${chartHeight} ${points
      .map((point) => `L${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
      .join(" ")} L${points[points.length - 1].x.toFixed(1)} ${chartHeight} Z`;
    const yourY = y(hoveredCutoffCell.yourScore);

    return { chartWidth, chartHeight, points, linePath, areaPath, yourY };
  }, [hoveredCutoffCell]);

  const hideCutoffPopover = React.useCallback(() => {
    setHoveredCutoffCell(null);
  }, []);

  React.useEffect(() => {
    window.addEventListener("resize", hideCutoffPopover);
    window.addEventListener("scroll", hideCutoffPopover, true);
    return () => {
      window.removeEventListener("resize", hideCutoffPopover);
      window.removeEventListener("scroll", hideCutoffPopover, true);
    };
  }, [hideCutoffPopover]);

  React.useLayoutEffect(() => {
    if (!hoveredCutoffCell) {
      return;
    }

    const popover = cutoffPopoverRef.current;
    if (!popover) {
      return;
    }

    const rect = hoveredCutoffCell.anchorRect;
    const popoverHeight = popover.offsetHeight;
    const viewportPadding = 8;
    const preferredLeft = rect.left + rect.width / 2 - CUTOFF_POPOVER_WIDTH / 2;
    const left = Math.max(
      viewportPadding,
      Math.min(preferredLeft, window.innerWidth - CUTOFF_POPOVER_WIDTH - viewportPadding),
    );
    const arrow = rect.left + rect.width / 2 - left;
    const aboveTop = rect.top - popoverHeight - 10;
    const belowTop = rect.bottom + 10;
    let placement: "above" | "below" = "above";
    let top = aboveTop;

    if (aboveTop < viewportPadding) {
      if (belowTop + popoverHeight <= window.innerHeight - viewportPadding) {
        placement = "below";
        top = belowTop;
      } else if (rect.top > window.innerHeight - rect.bottom) {
        placement = "above";
        top = Math.max(viewportPadding, aboveTop);
      } else {
        placement = "below";
        top = Math.min(belowTop, window.innerHeight - popoverHeight - viewportPadding);
      }
    }

    setCutoffPopoverPosition({
      left,
      top,
      arrow,
      placement,
    });
  }, [hoveredCutoffCell]);

  return (
    <div
      className={`rr-tab-panel active rr-heatmap-root${isFullscreen ? " rr-heatmap-panel-fullscreen" : ""}`}
    >
      <Tooltip
        target=".rr-course-header-label, .rr-college-name-tooltip"
        className="rr-course-tooltip"
        showDelay={100}
        hideDelay={80}
      />
      {hoveredCutoffCell && cutoffPopoverChart && (
        <div
          ref={cutoffPopoverRef}
          className={`rr-cutpop show ${cutoffPopoverPosition.placement}`}
          style={{
            left: `${cutoffPopoverPosition.left}px`,
            top: `${cutoffPopoverPosition.top}px`,
            "--arrow": `${cutoffPopoverPosition.arrow}px`,
          } as React.CSSProperties}
        >
          <div className="rr-cp-head">
            <div className="rr-cp-college">{hoveredCutoffCell.collegeName}</div>
            <span
              className="rr-cp-prob"
              style={{
                color: getProbabilityPillColors(hoveredCutoffCell.probability).foreground,
                background: getProbabilityPillColors(hoveredCutoffCell.probability).background,
              }}
            >
              {hoveredCutoffCell.probability}%
            </span>
          </div>
          <div className="rr-cp-branch">{hoveredCutoffCell.branch}</div>
          <div className="rr-cp-loc">{hoveredCutoffCell.location}</div>
          <div className="rr-cp-clabel">
            <span className="t">
              {hoveredCutoffCell.cutoffDataYears === 1
                ? "1 year of cutoff trend"
                : `${hoveredCutoffCell.cutoffDataYears} years of cutoff trends`}
            </span>
            <span
              className="rr-cp-trend"
              style={{
                color: hoveredTrendDelta >= 0 ? "#2F6A43" : "#B84A3C",
              }}
            >
              {hoveredTrendDelta >= 0 ? "▲ +" : "▼ "}
              {formatPercentile(hoveredTrendDelta)} {hoveredCutoffCell.unit}
            </span>
          </div>
          <div className="rr-cp-chart">
            <svg viewBox={`0 0 ${cutoffPopoverChart.chartWidth} ${cutoffPopoverChart.chartHeight + 18}`} preserveAspectRatio="none">
              <defs>
                <linearGradient id={cutoffChartGradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C66C2A" stopOpacity="0.26" />
                  <stop offset="100%" stopColor="#C66C2A" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <line
                x1={0}
                y1={cutoffPopoverChart.yourY}
                x2={cutoffPopoverChart.chartWidth}
                y2={cutoffPopoverChart.yourY}
                stroke="#4A8294"
                strokeWidth={1.3}
                strokeDasharray="4 3"
              />
              <text x={2} y={cutoffPopoverChart.yourY - 4} fontSize={8.5} fill="#4A8294" fontWeight={600} textAnchor="start">
                YOU {formatPercentile(hoveredCutoffCell.yourScore)}
              </text>
              <path d={cutoffPopoverChart.areaPath} fill={`url(#${cutoffChartGradientId})`} />
              <path d={cutoffPopoverChart.linePath} fill="none" stroke="#C66C2A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              {cutoffPopoverChart.points.map((point, index) => (
                <circle
                  key={`${point.year}-${point.value}`}
                  cx={point.x}
                  cy={point.y}
                  r={index === cutoffPopoverChart.points.length - 1 ? 3.4 : 2.4}
                  fill={index === cutoffPopoverChart.points.length - 1 ? "#C66C2A" : "#fff"}
                  stroke="#C66C2A"
                  strokeWidth={1.6}
                />
              ))}
              <text
                x={cutoffPopoverChart.points[cutoffPopoverChart.points.length - 1].x}
                y={cutoffPopoverChart.points[cutoffPopoverChart.points.length - 1].y - 7}
                fontSize={9.5}
                fontWeight={700}
                fill="#A8531C"
                textAnchor="end"
              >
                {formatPercentile(hoveredCutoffCell.latestCutoff)}
              </text>
              {cutoffPopoverChart.points.map((point) => (
                <text
                  key={`year-${point.year}`}
                  x={point.x}
                  y={cutoffPopoverChart.chartHeight + 13}
                  fontSize={8.5}
                  fill="#8A8F9F"
                  textAnchor="middle"
                >
                  {String(point.year).slice(-2)}
                </text>
              ))}
            </svg>
          </div>
          <div className="rr-cp-foot">
            <div className="rr-cp-stat">
              <span className="k">'{String(hoveredCutoffCell.latestYear).slice(-2)} Cutoff</span>
              <span className="v">
                {formatPercentile(hoveredCutoffCell.latestCutoff)}<small>{hoveredCutoffCell.unit}</small>
              </span>
            </div>
            <div className="rr-cp-stat">
              <span className="k">{hoveredCutoffCell.cutoffDataYears}-yr swing</span>
              <span className="v">
                {hoveredTrendDelta >= 0 ? "+" : ""}
                {formatPercentile(hoveredTrendDelta)}
              </span>
            </div>
            <div className="rr-cp-stat">
              <span className="k">Your chance</span>
              <span className="v" style={{ color: getProbabilityPillColors(hoveredCutoffCell.probability).foreground }}>
                {hoveredCutoffCell.probability}%
              </span>
            </div>
          </div>
          <div className="rr-cp-legend">
            <span className="sw" />
            Your percentile - {formatPercentile(hoveredCutoffCell.yourScore)}
          </div>
        </div>
      )}
      {status === "loading" && (
        <div className="rr-loading-overlay" aria-live="polite" aria-busy="true">
          <ProgressSpinner style={{ width: "2.2rem", height: "2.2rem" }} strokeWidth="6" animationDuration=".9s" />
          <span className="rr-loading-text">{loadingOverlayText}</span>
        </div>
      )}

      <div ref={pdfCaptureRef} className="rr-heatmap-pdf-capture">
      <div className="rr-heatmap-pdf-letterhead" aria-hidden="true">
        <span className="rr-heatmap-pdf-letterhead-name">{pdfExporterDisplayName}</span>
        <img src={hallticketWordmarkDark} alt="" className="rr-heatmap-pdf-letterhead-logo" />
      </div>
      {(title || subtitle) && (
        <div className="rr-section-hdr">
          {title ? <h2>{title}</h2> : null}
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      )}

      {noTableData && (
        <div className="rr-heatmap-wrap rr-heatmap-wrap--empty-state">
          <HapEmptyNoData
            examLabel={data.candidate.exam}
            percentile={data.candidate.percentile}
            category={data.candidate.category}
          />
        </div>
      )}

      {!noTableData && <div className="rr-heatmap-controls">
        <div className="rr-ctrl-group">
          <span className="rr-ctrl-label">Sort by</span>
          <div className="rr-ctrl-field">
            <Dropdown
              className="rr-ctrl-select"
              panelClassName="rr-dropdown-panel"
              value={sortMode}
              options={[
                { label: "Average Likelihood", value: "avg" },
                { label: "Name (A-Z)", value: "name" },
              ]}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => onSortModeChange(e.value as ResultReviewSort)}
            />
          </div>
        </div>
        <div className="rr-ctrl-divider" />
        <div className="rr-ctrl-group">
          <span className="rr-ctrl-label">{filterMode === "college" ? "Filter colleges" : "Filter branch"}</span>
          <div className="rr-ctrl-field">
            <MultiSelect
              className="rr-ctrl-select"
              panelClassName="rr-dropdown-panel"
              value={filterMode === "college" ? collegeMultiSelectValue : courseMultiSelectValue}
              options={
                filterMode === "college"
                  ? collegeFilterOptions
                  : courseFilterDropdownOptions
              }
              optionLabel="label"
              optionValue="value"
              optionDisabled={filterMode === "course" ? "disabled" : undefined}
              onChange={(e) => {
                if (filterMode === "college") {
                  const rawNext = ((e.value as string[]) ?? []).filter((value) => value !== SELECT_ALL_COLLEGES_VALUE);
                  const requestedSelectAll = ((e.value as string[]) ?? []).includes(SELECT_ALL_COLLEGES_VALUE);
                  let next = rawNext;

                  if (requestedSelectAll) {
                    if (!isAllCollegesSelected) {
                      next = allCollegeNames;
                    } else {
                      next = rawNext;
                    }
                  } else {
                    if (isAllCollegesSelected) {
                      next = [];
                    } else {
                      next = rawNext;
                    }
                  }

                  onCollegeFilterChange?.(next);
                } else {
                  const rawNext = ((e.value as string[]) ?? [])
                    .filter((value) => value !== SELECT_ALL_COURSES_VALUE)
                    .filter((value) => selectableCourseNames.includes(value));
                  const requestedSelectAll = ((e.value as string[]) ?? []).includes(SELECT_ALL_COURSES_VALUE);
                  let next = rawNext;

                  if (requestedSelectAll) {
                    if (!isAllCoursesSelected) {
                      next = selectableCourseNames;
                    } else {
                      next = rawNext;
                    }
                  } else {
                    if (isAllCoursesSelected) {
                      next = [];
                    } else {
                      next = rawNext;
                    }
                  }

                  onCourseFilterChange(next);
                }
              }}
              placeholder={filterMode === "college" ? "Filter colleges" : "All branches"}
              maxSelectedLabels={0}
              selectedItemsLabel={filterMode === "college" ? "Filter colleges" : "Filter branch"}
              showClear={false}
            />
          </div>
        </div>
        {showLocationFilter && (
          <>
            <div className="rr-ctrl-divider" />
            <div className="rr-ctrl-group">
              <span className="rr-ctrl-label">Filter Location</span>
              <div className="rr-ctrl-field rr-ctrl-field--location">
                <Dropdown
                  className="rr-ctrl-select"
                  panelClassName="rr-dropdown-panel"
                  value={locationFilter}
                  options={locationOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => onLocationFilterChange?.((e.value as string) ?? ALL_LOCATIONS_VALUE)}
                />
              </div>
            </div>
          </>
        )}
        <div className="rr-heatmap-actions">
          <div className="rr-heatmap-stat">
            {statText ?? (
              <>
                Showing <strong>{visibleCourseIndexes.length}</strong> Branches across <strong>{resultRows.length}</strong> colleges
              </>
            )}
          </div>
          <div className="rr-heatmap-actions-toolbar rr-pdf-exclude">
            {trailingActionButton && <div className="rr-heatmap-actions-top">{trailingActionButton}</div>}
            <div className="rr-heatmap-actions-bottom">
              <button
                type="button"
                className="rr-save-pdf-btn"
                disabled={!canSavePdf || savingPdf}
                onClick={() => openPdfExportDialog()}
                aria-label="Save result review as PDF"
                aria-busy={savingPdf}
                title="Save as PDF"
              >
                {savingPdf ? (
                  <ProgressSpinner style={{ width: "1rem", height: "1rem" }} strokeWidth="6" animationDuration=".9s" />
                ) : (
                  <FloppyDisk size={16} weight="bold" />
                )}
              </button>
              <button
                type="button"
                className="rr-fullscreen-toggle"
                onClick={() => setIsFullscreen((prev) => !prev)}
                aria-label={isFullscreen ? "Exit table fullscreen" : "Open table fullscreen"}
                title={isFullscreen ? "Exit fullscreen" : "Open fullscreen"}
              >
                {isFullscreen ? <ArrowsInSimple size={16} weight="bold" /> : <ArrowsOutSimple size={16} weight="bold" />}
              </button>
            </div>
          </div>
        </div>
      </div>}

      {!noTableData && (
        <div
          className={
            showHeatmapTable
              ? "rr-heatmap-wrap"
              : "rr-heatmap-wrap rr-heatmap-wrap--empty-state"
          }
        >
          {showHeatmapTable ? (
            <table className="rr-heatmap-table">
              <colgroup>
                <col className="rr-col-college" />
                {visibleCourseIndexes.map((idx) => (
                  <col key={data.courses[idx]?.course_name ?? idx} className="rr-col-hap" />
                ))}
                <col className="rr-col-avg" />
              </colgroup>
              <thead>
                <tr>
                  <th>College</th>
                  {visibleCourseIndexes.map((idx) => (
                    <th key={data.courses[idx]?.course_name} className="rr-course-header" data-rr-pdf-course-idx={idx}>
                      <span
                        className="rr-course-header-label"
                        data-pr-tooltip={data.courses[idx]?.course_name}
                        data-pr-position="top"
                      >
                        {getCourseTableHeading(data.courses[idx])}
                      </span>
                    </th>
                  ))}
                  <th className="rr-avg-col">AVG %</th>
                </tr>
              </thead>
              <tbody>
                {resultRows.map((row: { college: HapTableCollege; averageProbability: number }) => (
                  <tr key={row.college.college_code}>
                    <td className="rr-college-cell">
                      <div
                        className="rr-college-cell-name rr-college-name-tooltip"
                        data-pr-tooltip={row.college.college_name}
                        data-pr-position="top"
                      >
                        {row.college.college_name}
                      </div>
                      <div className="rr-college-cell-loc">
                        <MapPin size={12} weight="fill" aria-hidden="true" />
                        {getCollegeLocation(row.college)}
                      </div>
                    </td>

                    {visibleCourseIndexes.map((idx) => {
                      const cell = row.college.cells[idx];
                      if (!cell) {
                        return (
                          <td
                            key={`${row.college.college_code}-${idx}`}
                            className="rr-heat-cell rr-cell-na"
                            data-rr-pdf-course-idx={idx}
                          >
                            <span className="rr-cell-pct">—</span>
                          </td>
                        );
                      }

                      const band = getProbabilityBand(cell.hap_probability);
                      const scoreUnit = resolveScoreUnit(data.candidate.exam);
                      const trend = resolveCutoffTrend(cell, trendEndYear);
                      const cutoffDataYears = (cell.yearly_cutoffs ?? []).length;
                      const latestCutoffPoint = trend[trend.length - 1];
                      const latestCutoff = latestCutoffPoint?.value ?? cell.cutoff_percentile;
                      const latestYear = latestCutoffPoint?.year ?? trendEndYear;
                      const probability = Number.isFinite(cell.hap_probability)
                        ? Math.round(cell.hap_probability)
                        : Number(formatPercentile(cell.hap_probability));

                      return (
                        <td
                          key={`${row.college.college_code}-${idx}`}
                          className={`rr-heat-cell rr-band-${band}`}
                          data-rr-pdf-course-idx={idx}
                          onMouseEnter={(event) => {
                            setHoveredCutoffCell({
                              anchorRect: event.currentTarget.getBoundingClientRect(),
                              collegeName: row.college.college_name,
                              location: getCollegeLocation(row.college),
                              branch: data.courses[idx]?.course_name ?? "",
                              probability,
                              latestCutoff,
                              latestYear,
                              yourScore: data.candidate.percentile,
                              unit: scoreUnit,
                              trend,
                              cutoffDataYears,
                            });
                          }}
                          onMouseLeave={hideCutoffPopover}
                        >
                          <span className="rr-cell-pct">
                            {Number.isFinite(cell.hap_probability)
                              ? Math.round(cell.hap_probability)
                              : formatPercentile(cell.hap_probability)}%
                          </span>
                        </td>
                      );
                    })}

                    <td className="rr-avg-cell">
                      <div className="rr-avg-cell-val">{formatPercentile(row.averageProbability)}%</div>
                      <div className="rr-avg-cell-bar">
                        <div
                          className="rr-avg-cell-fill"
                          style={{ width: `${Math.min(100, row.averageProbability)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <HapEmptyFiltered onResetFilters={handleResetFilters} />
          )}
        </div>
      )}

      {showHeatmapTable && (
        <div className="rr-heatmap-legend-container">
          <div className="rr-heatmap-legend">
            <span className="rr-legend-label">Probability of getting in:</span>
            <div className="rr-legend-item">
              <div className="rr-legend-swatch rr-band-4" />
              <span className="rr-legend-text">&gt;75% - Strong</span>
            </div>
            <div className="rr-legend-item">
              <div className="rr-legend-swatch rr-band-3" />
              <span className="rr-legend-text">50-75% - Possible</span>
            </div>
            <div className="rr-legend-item">
              <div className="rr-legend-swatch rr-band-2" />
              <span className="rr-legend-text">25-50% - Difficult</span>
            </div>
            <div className="rr-legend-item">
              <div className="rr-legend-swatch rr-band-1" />
              <span className="rr-legend-text">&lt;25% - Unlikely</span>
            </div>
            <div className="rr-legend-item">
              <div className="rr-legend-swatch rr-na-swatch" />
              <span className="rr-legend-text">Not offered</span>
            </div>
          </div>
          <div className="rr-heatmap-warning">
            Warning: these are AI generated results based on past data. Actual possibilities will vary.
          </div>
        </div>
      )}
      <div className="rr-heatmap-pdf-footer" aria-hidden="true">https://hallticket.in</div>
      </div>

      <Dialog
        className="rr-pdf-export-dialog"
        header="Courses in PDF"
        visible={pdfExportDialogVisible}
        modal
        draggable={false}
        resizable={false}
        style={{ width: "min(520px, 94vw)" }}
        onHide={() => {
          if (!savingPdf) {
            setPdfExportDialogVisible(false);
          }
        }}
        footer={
          <div className="rr-pdf-export-dialog-footer">
            <Button
              type="button"
              label="Cancel"
              text
              disabled={savingPdf}
              onClick={() => setPdfExportDialogVisible(false)}
            />
            <Button
              type="button"
              label={savingPdf ? "Saving…" : "Save PDF"}
              icon={savingPdf ? undefined : "pi pi-file-pdf"}
              disabled={savingPdf || pdfExportSelectedCourses.length === 0}
              onClick={() => void handlePdfExportConfirm()}
            />
          </div>
        }
      >
        <p className="rr-pdf-export-dialog-hint">
          Pick up to {PDF_EXPORT_MAX_COURSES} courses to include as columns in the export ({pdfExportSelectedCourses.length}/{PDF_EXPORT_MAX_COURSES} selected).
        </p>
        <MultiSelect
          className="rr-ctrl-select rr-pdf-export-course-ms"
          panelClassName="rr-dropdown-panel"
          value={pdfExportSelectedCourses}
          options={pdfExportCourseOptions}
          optionLabel="label"
          optionValue="value"
          display="chip"
          filter
          placeholder="Select courses"
          maxSelectedLabels={4}
          selectedItemsLabel="{0} courses"
          onChange={(e) => {
            const next = (e.value as string[]) ?? [];
            setPdfExportSelectedCourses(next.slice(0, PDF_EXPORT_MAX_COURSES));
          }}
        />
      </Dialog>
    </div>
  );
}
